import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchMeetings, createMeeting, attachAudio, processMeeting, fetchActions, setActionStatus,
  fetchDrafts, updateDraft, approveDraft, sendDraft, fetchAccountOptions,
  type Meeting, type Action, type Draft,
} from '../lib/scribe';
import { onFail } from '../lib/errors';
import './scribe.css';

/* =====================================================================================
   Scribe: the meeting goes in, the follow-through comes out.

   Capture is three ways: record here, upload a recording, or paste the notes. Processing
   happens on the edge; what comes back is a summary, the decisions, the actions (each with
   the sentence it came from) and drafted follow-up emails. Actions with dates are nagged by
   the scheduler until closed. Drafts are exactly that: a person reads, edits, approves, and
   until the mail integration exists the Open-in-mail button hands the approved text to your
   own mail client, so nothing is ever sent by software on its own.
   ===================================================================================== */

const fmtDate = (s: string | null | undefined) => (s ? String(s).slice(0, 10) : '');
const STATUS_PILL: Record<string, string> = {
  captured: 'mute', transcribing: 'info', extracting: 'info', ready: 'ok', failed: 'warn',
};

export function ScribeView() {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [actions, setActions] = useState<Action[]>([]);
  const [capturing, setCapturing] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(() => {
    fetchMeetings().then(setMeetings).catch(onFail('Meetings', () => setMeetings([])));
    fetchActions().then(setActions).catch(() => setActions([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = meetings.find((m) => m.id === openId) ?? null;
  const openActions = actions.filter((a) => a.status === 'open');

  async function process(id: string) {
    setBusyId(id); setMsg('Processing. A recording takes a minute or two.');
    const r = await processMeeting(id);
    setBusyId(null);
    setMsg(r.error ? r.error
      : `Done: ${r.actions ?? 0} action(s), ${r.decisions ?? 0} decision(s), `
        + `${r.drafts ?? 0} email draft(s) waiting for your approval.`);
    load();
  }

  return (
    <div className="page scribe">
      <div className="page-head">
        <h1 className="page-title">Scribe</h1>
        <p className="page-sub">
          Record a meeting, upload one, or paste the notes. Scribe writes the minutes, pulls
          out the decisions and actions, drafts the follow-up emails, and nags the actions
          until they are done. Drafts are sent by you, never by the software.
        </p>
      </div>

      {msg && <p className="block-note" role="status">{msg}</p>}

      {openActions.length > 0 && (
        <section className="card scr-actions">
          <h2>Open actions from meetings</h2>
          {openActions.map((a) => (
            <div key={a.id} className="scr-action">
              <label className="scr-tick">
                <input type="checkbox" onChange={() =>
                  setActionStatus(a.id, 'done').then(load).catch(onFail('The action'))} />
                <span>
                  <strong>{a.title}</strong>
                  {a.owner_name && <> · {a.owner_name}</>}
                  {a.due_on && <span className={new Date(a.due_on) < new Date() ? 'scr-late' : 'muted'}>
                    {' '}· due {fmtDate(a.due_on)}</span>}
                  {a.source_quote && <div className="scr-quote">&ldquo;{a.source_quote}&rdquo;</div>}
                </span>
              </label>
            </div>
          ))}
        </section>
      )}

      <div className="toolbar">
        <button className="btn primary" onClick={() => setCapturing(true)}>Capture a meeting</button>
      </div>

      <section className="card">
        {meetings.length === 0 && (
          <p className="module-empty">
            No meetings yet. Capture one and Scribe does the part everyone skips: the minutes,
            the actions, and the follow-up.
          </p>
        )}
        {meetings.map((m) => (
          <div key={m.id} className="scr-row" onClick={() => setOpenId(m.id)}>
            <div>
              <span className="mono">{m.reference}</span> <strong>{m.title}</strong>
              <span className={`pill ${STATUS_PILL[m.status] ?? 'mute'}`}>{m.status}</span>
              <div className="cell-sub">
                {fmtDate(m.held_at)}
                {m.attendees?.length > 0 && <> · {m.attendees.map((a) => a.name).join(', ')}</>}
              </div>
              {m.status === 'failed' && m.error && <div className="scr-err">{m.error}</div>}
            </div>
            <div className="scr-row-side" onClick={(e) => e.stopPropagation()}>
              {(m.status === 'captured' || m.status === 'failed') && (
                <button className="btn" disabled={busyId === m.id}
                        onClick={() => process(m.id)}>
                  {busyId === m.id ? 'Working...' : 'Process'}
                </button>
              )}
              <button className="link-btn" onClick={() => setOpenId(m.id)}>Open</button>
            </div>
          </div>
        ))}
      </section>

      {capturing && (
        <CaptureForm onClose={() => setCapturing(false)}
                     onDone={(m, andProcess) => {
                       setCapturing(false); load();
                       if (andProcess) void process(m.id); else setMsg(`${m.reference} captured.`);
                     }} />
      )}
      {open && (
        <MeetingDrawer m={open}
                       actions={actions.filter((a) => a.meeting_id === open.id)}
                       onClose={() => setOpenId(null)} onChanged={load} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ capture */

function CaptureForm({ onClose, onDone }: {
  onClose: () => void; onDone: (m: Meeting, andProcess: boolean) => void;
}) {
  const [f, setF] = useState({
    title: '', heldAt: new Date().toISOString().slice(0, 16), medium: 'video', location: '',
    accountId: '', notes: '',
  });
  /* share defaults OFF: a follow-up draft is written only for people somebody deliberately
     ticked, so an external meeting is never circulated by default. */
  const [attendees, setAttendees] = useState([{ name: '', email: '', organisation: '', share: false }]);
  const [mode, setMode] = useState<'record' | 'upload' | 'notes'>('record');
  const [accounts, setAccounts] = useState<{ id: string; name: string }[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [recording, setRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [seconds, setSeconds] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const rec = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => { fetchAccountOptions().then(setAccounts).catch(() => {}); }, []);
  useEffect(() => () => { rec.current?.stop(); if (timer.current) clearInterval(timer.current); }, []);

  const set = (k: string) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  async function toggleRecord() {
    if (recording) {
      rec.current?.stop();
      return;
    }
    setErr(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunks.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecordedBlob(new Blob(chunks.current, { type: 'audio/webm' }));
        setRecording(false);
        if (timer.current) clearInterval(timer.current);
      };
      mr.start(1000);
      rec.current = mr;
      setRecording(true); setSeconds(0);
      timer.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch {
      setErr('The microphone was refused. Upload a recording or paste the notes instead.');
    }
  }

  const audio = recordedBlob ?? file;
  const canSave = f.title.trim() && (f.notes.trim() || audio);

  async function save() {
    setBusy(true); setErr(null);
    try {
      const m = await createMeeting({
        title: f.title, heldAt: f.heldAt, medium: f.medium, location: f.location,
        attendees, accountId: f.accountId || null,
        transcript: f.notes, transcriptSource: 'pasted',
      });
      if (audio) {
        await attachAudio(m.id, audio, (audio as File).type || 'audio/webm');
      }
      onDone(m, true);
    } catch (e) { setErr(String((e as Error).message)); setBusy(false); }
  }

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer scr-drawer">
        <h2>Capture a meeting</h2>
        {err && <p className="block-note">{err}</p>}
        <label className="cf-field"><span>What was the meeting</span>
          <input className="input" value={f.title} onChange={set('title')}
                 placeholder="P&T pre-read with Dr Marsh" /></label>
        <div className="scr-two">
          <label className="cf-field"><span>When</span>
            <input className="input" type="datetime-local" value={f.heldAt} onChange={set('heldAt')} /></label>
          <label className="cf-field"><span>How</span>
            <select className="input sel" value={f.medium} onChange={set('medium')}>
              <option value="video">Video call</option>
              <option value="in_person">In person</option>
              <option value="phone">Phone</option>
              <option value="congress">At a congress</option>
              <option value="other">Other</option>
            </select></label>
        </div>
        <label className="cf-field"><span>Linked account (files the meeting on their record)</span>
          <select className="input sel" value={f.accountId} onChange={set('accountId')}>
            <option value="">None</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select></label>

        <div className="scr-section">
          <div className="scr-section-head">
            <span>Who was there</span>
            <span className="scr-section-hint">Tick <strong>Follow-up</strong> only for people
            who should receive the follow-up email. Untick and nothing is ever drafted to
            them: external meetings stay uncirculated unless you choose otherwise.</span>
          </div>
          <div className="scr-att-grid scr-att-header" aria-hidden="true">
            <span>Name</span><span>Email</span><span>Organisation</span>
            <span className="scr-att-share">Follow-up</span><span />
          </div>
          {attendees.map((a, i) => (
            <div key={i} className="scr-att-grid">
              <input className="input" placeholder="Name" value={a.name}
                     onChange={(e) => setAttendees(attendees.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
              <input className="input" placeholder="Needed for a follow-up" value={a.email}
                     onChange={(e) => setAttendees(attendees.map((x, j) => j === i ? { ...x, email: e.target.value } : x))} />
              <input className="input" placeholder="Organisation" value={a.organisation}
                     onChange={(e) => setAttendees(attendees.map((x, j) => j === i ? { ...x, organisation: e.target.value } : x))} />
              <label className="scr-att-share" title="Draft a follow-up email to this person">
                <input type="checkbox" checked={a.share ?? false}
                       onChange={(e) => setAttendees(attendees.map((x, j) => j === i ? { ...x, share: e.target.checked } : x))} />
              </label>
              <button className="scr-att-remove" title="Remove this person"
                      onClick={() => setAttendees(attendees.length > 1
                        ? attendees.filter((_, j) => j !== i)
                        : [{ name: '', email: '', organisation: '', share: false }])}>×</button>
            </div>
          ))}
          <button className="link-btn" onClick={() =>
            setAttendees([...attendees, { name: '', email: '', organisation: '', share: false }])}>
            + add another person
          </button>
        </div>

        <div className="scr-section">
          <div className="scr-section-head"><span>The meeting itself</span></div>
          <div className="seg scr-mode-seg">
            <button className={mode === 'record' ? 'on' : ''} onClick={() => setMode('record')}>Record</button>
            <button className={mode === 'upload' ? 'on' : ''} onClick={() => setMode('upload')}>Upload</button>
            <button className={mode === 'notes' ? 'on' : ''} onClick={() => setMode('notes')}>Type or paste</button>
          </div>
          {mode === 'record' && (
            <div className="scr-capture-modes">
              <button className={`btn ${recording ? 'scr-recording' : 'primary'}`} onClick={toggleRecord}>
                {recording ? `■ Stop (${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')})`
                  : recordedBlob ? 'Re-record' : '● Record now'}
              </button>
              {recordedBlob && !recording && <span className="pill ok">recording captured</span>}
            </div>
          )}
          {mode === 'upload' && (
            <div className="scr-capture-modes">
              <label className="btn scr-upload">
                {file ? file.name : 'Choose an audio or video file'}
                <input type="file" accept="audio/*,video/webm,video/mp4" hidden
                       onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
              </label>
              {file && <span className="pill ok">ready</span>}
            </div>
          )}
          {mode === 'notes' && (
            <textarea className="input" rows={7} value={f.notes} onChange={set('notes')}
                      placeholder="Paste the transcript or your own notes, decisions and promises included: the scribe works from exactly what is here." />
          )}
          {audio && f.notes.trim() && (
            <p className="muted scr-note">Notes take precedence: the recording is kept but the
              notes are what get processed.</p>
          )}
        </div>

        <div className="exp-decide-actions">
          <button className="btn primary" disabled={busy || !canSave} onClick={save}>
            {busy ? 'Saving...' : 'Save and process'}
          </button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ detail */

function MeetingDrawer({ m, actions, onClose, onChanged }: {
  m: Meeting; actions: Action[]; onClose: () => void; onChanged: () => void;
}) {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [tab, setTab] = useState<'summary' | 'minutes' | 'transcript'>('summary');
  const [err, setErr] = useState<string | null>(null);
  const load = useCallback(() => {
    fetchDrafts(m.id).then(setDrafts).catch(() => setDrafts([]));
  }, [m.id]);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer scr-drawer">
        <h2>{m.reference} <span className={`pill ${STATUS_PILL[m.status] ?? 'mute'}`}>{m.status}</span></h2>
        <p className="muted">{m.title} · {fmtDate(m.held_at)}</p>
        {err && <p className="block-note">{err}</p>}

        <div className="seg scr-tabs">
          <button className={tab === 'summary' ? 'on' : ''} onClick={() => setTab('summary')}>Summary</button>
          <button className={tab === 'minutes' ? 'on' : ''} onClick={() => setTab('minutes')}>Minutes</button>
          <button className={tab === 'transcript' ? 'on' : ''} onClick={() => setTab('transcript')}>Source</button>
        </div>

        {tab === 'summary' && (
          <>
            {m.summary ? <p className="scr-body">{m.summary}</p>
              : <p className="muted">Not processed yet.</p>}
            {m.decisions?.length > 0 && (
              <section className="scr-block"><h3>Decisions</h3>
                <ul>{m.decisions.map((d, i) => <li key={i}>{d}</li>)}</ul></section>
            )}
            {actions.length > 0 && (
              <section className="scr-block"><h3>Actions</h3>
                {actions.map((a) => (
                  <div key={a.id} className="scr-action">
                    <label className="scr-tick">
                      <input type="checkbox" checked={a.status === 'done'}
                             onChange={(e) => setActionStatus(a.id, e.target.checked ? 'done' : 'open')
                               .then(onChanged).catch(onFail('The action'))} />
                      <span className={a.status === 'done' ? 'scr-done' : ''}>
                        <strong>{a.title}</strong>
                        {a.owner_name && <> · {a.owner_name}</>}
                        {a.due_on && <> · due {fmtDate(a.due_on)}</>}
                      </span>
                    </label>
                  </div>
                ))}
              </section>
            )}
          </>
        )}
        {tab === 'minutes' && (
          m.minutes ? <pre className="scr-pre">{m.minutes}</pre>
            : <p className="muted">Not processed yet.</p>
        )}
        {tab === 'transcript' && (
          m.transcript ? <pre className="scr-pre">{m.transcript}</pre>
            : <p className="muted">{m.audio_path
                ? 'A recording is attached; the transcript appears once processed.'
                : 'No source text.'}</p>
        )}

        {drafts.length > 0 && (
          <section className="scr-block">
            <h3>Follow-up drafts</h3>
            <p className="muted scr-note">
              Written by the scribe, sent by nobody. Edit, approve, and use Open in mail to
              send it from your own account.
            </p>
            {drafts.map((d) => (
              <DraftCard key={d.id} d={d}
                         onChanged={() => { load(); }}
                         onErr={setErr} />
            ))}
          </section>
        )}
      </div>
    </>
  );
}

function DraftCard({ d, onChanged, onErr }: {
  d: Draft; onChanged: () => void; onErr: (s: string | null) => void;
}) {
  const [edit, setEdit] = useState(false);
  const [to, setTo] = useState(d.to_email);
  const [subject, setSubject] = useState(d.subject);
  const [body, setBody] = useState(d.body);
  const approved = d.status !== 'draft';

  async function saveEdits() {
    try { await updateDraft(d.id, { to_email: to, subject, body }); setEdit(false); onChanged(); }
    catch (e) { onErr(String((e as Error).message)); }
  }

  return (
    <div className="scr-draft">
      <div className="scr-draft-head">
        <strong>{d.to_name || to || 'No recipient yet'}</strong>
        <span className={`pill ${approved ? 'ok' : 'mute'}`}>{d.status}</span>
      </div>
      {edit ? (
        <>
          <input className="input" value={to} placeholder="recipient@example.com"
                 onChange={(e) => setTo(e.target.value)} />
          <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} />
          <textarea className="input" rows={6} value={body} onChange={(e) => setBody(e.target.value)} />
          <div className="exp-decide-actions">
            <button className="btn" onClick={saveEdits}>Save</button>
            <button className="btn" onClick={() => setEdit(false)}>Cancel</button>
          </div>
        </>
      ) : (
        <>
          <div className="scr-draft-subject">{subject}</div>
          <pre className="scr-pre scr-draft-body">{body}</pre>
          <div className="exp-decide-actions">
            {!approved && <button className="btn" onClick={() => setEdit(true)}>Edit</button>}
            {!approved && (
              <button className="btn primary" onClick={async () => {
                try { await approveDraft(d.id); onErr(null); onChanged(); }
                catch (e) { onErr(String((e as Error).message)); }
              }}>Approve</button>
            )}
            {(d.status === 'queued' || d.status === 'failed') && (
              /* Three gates by design: the model drafted, a person approved, a person sends.
                 Where the tenant has no Graph secrets this reports exactly that and the
                 mailto route below still works. */
              <button className="btn primary" onClick={async () => {
                const r = await sendDraft(d.id);
                onErr(r.error ? r.error : null);
                if (r.ok) onErr(null);
                onChanged();
              }}>{d.status === 'failed' ? 'Try again' : 'Send now'}</button>
            )}
            {d.status === 'sent' && <span className="pill ok">sent</span>}
            <a className="btn" href={`mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`}>
              Open in mail
            </a>
          </div>
        </>
      )}
    </div>
  );
}
